import { isWeekend } from "date-fns";
import { call, select } from "redux-saga/effects";

import { EntityType } from "@mapmycustomers/shared";
import LeaderboardMetricFieldName from "@mapmycustomers/shared/enum/fieldModel/LeaderboardMetricFieldName";
import PlatformFilterOperator from "@mapmycustomers/shared/enum/PlatformFilterOperator";
import { AnyEntity } from "@mapmycustomers/shared/types/entity";
import LeaderboardItem from "@mapmycustomers/shared/types/entity/LeaderboardItem";
import Organization from "@mapmycustomers/shared/types/Organization";
import Team from "@mapmycustomers/shared/types/Team";
import ListResponse from "@mapmycustomers/shared/types/viewModel/ListResponse";

import { getDatesRange } from "@app/enum/dashboard/dateRanges";
import NewRecordsCardData from "@app/scene/dashboard/store/cards/newRecords/NewRecordsCardData";
import { callApi } from "@app/store/api/callApi";
import { getOrganizationId } from "@app/store/iam";
import { getTeamsOfEntireOrganization } from "@app/store/members";
import EntityTypesSupportedInNewRecords from "@app/types/dashboard/cards/newRecords/EntityTypesSupportedInNewRecords";
import NewRecordsCardConfiguration from "@app/types/dashboard/cards/newRecords/NewRecordsCardConfiguration";
import Scope from "@app/types/dashboard/Scope";
import AggregatedListResponse from "@app/types/viewModel/AggregatedListResponse";
import { doesWeekStartFromMonday, getLocalTimeZone } from "@app/util/dates";
import { parseApiDate } from "@app/util/parsers";

import { DateAggregationItem, fetchMethodByEntityType, granularityByDateRange } from "./common";

const newRecordsMetricByEntityType: Record<
  EntityTypesSupportedInNewRecords,
  LeaderboardMetricFieldName
> = {
  [EntityType.COMPANY]: LeaderboardMetricFieldName.COMPANIES_CREATED,
  [EntityType.DEAL]: LeaderboardMetricFieldName.DEALS_CREATED,
  [EntityType.PERSON]: LeaderboardMetricFieldName.PEOPLE_CREATED,
};

export function* fetchNewRecordsCardDataHelper(params: {
  callback: (data: NewRecordsCardData) => void;
  configuration: NewRecordsCardConfiguration;
  scope?: Scope;
}) {
  const { callback, configuration, scope } = params;
  const { criteria, dateRange, preferences } = configuration;

  const teams: Team[] = yield select(getTeamsOfEntireOrganization);
  const orgId: Organization["id"] = yield select(getOrganizationId);

  const isTeamScope = !!scope?.teamId && !scope?.userId;
  const scopeTeam = isTeamScope ? teams.find(({ id }) => id === scope.teamId) : undefined;

  const granularity = granularityByDateRange[dateRange.range];
  const range = getDatesRange(dateRange.range, dateRange.subRange);

  const createdByUserIds = scope?.userId ? [scope.userId] : scopeTeam?.users.map((user) => user.id);

  const filters = {
    ...configuration.extraFilters,
    createdAt: { $gte: range.startDate.toISOString(), $lte: range.endDate.toISOString() },
    createdBy: createdByUserIds
      ? { [PlatformFilterOperator.CONTAINS]: createdByUserIds }
      : undefined,
    source: criteria.sources.length ? { $in: criteria.sources } : undefined,
  };

  const fetchMethod = fetchMethodByEntityType[criteria.entityType];

  const payload = {
    $aggs: preferences.showChart
      ? {
          count: ["id"],
          primaryGroup: {
            field: "createdAt",
            interval: granularity,
            offset: granularity !== "week" || doesWeekStartFromMonday() ? undefined : "-1 day",
            timezone: getLocalTimeZone(),
          },
        }
      : undefined,
    $filters: filters,
    $limit: 0,
  };

  const response: AggregatedListResponse<AnyEntity, DateAggregationItem[] | undefined> =
    yield callApi(fetchMethod, orgId, payload);

  let sum = 0;
  const result: NewRecordsCardData = {
    count: response.total,
    history: response.aggregations?.map((item) => {
      sum += item.doc_count;
      return {
        count: preferences.cumulative ? sum : item.doc_count,
        date: parseApiDate(item.key),
      };
    }),
  };

  if (preferences.showTrend) {
    // fetch again, but for the previous range
    const previousRange = getDatesRange(dateRange.range, dateRange.subRange, true);
    const payload = {
      $aggs: preferences.showChart
        ? {
            count: ["id"],
            primaryGroup: {
              field: "createdAt",
              interval: granularity,
              offset: granularity !== "week" || doesWeekStartFromMonday() ? undefined : "-1 day",
              timezone: getLocalTimeZone(),
            },
          }
        : undefined,
      $filters: {
        ...filters,
        createdAt: {
          $gte: previousRange.startDate.toISOString(),
          $lte: previousRange.endDate.toISOString(),
        },
      },
      $limit: 0,
    };
    const response: AggregatedListResponse<AnyEntity, DateAggregationItem[] | undefined> =
      yield callApi(fetchMethod, orgId, payload);
    result.previousCount = response.total;
    if (preferences.showChart) {
      let sum = 0;
      result.previousHistory = response.aggregations?.map((item) => {
        sum += item.doc_count;
        return {
          count: preferences.cumulative ? sum : item.doc_count,
          date: parseApiDate(item.key),
        };
      });
    }
  }

  const filteredHistory = result.history?.filter(
    ({ count, date }, index) =>
      !isWeekend(date) || count || (result.previousHistory && result.previousHistory[index]?.count)
  );

  const filteredPreviousHistory = result.previousHistory?.filter(
    ({ count }, index) =>
      count ||
      (result.history && (result.history[index]?.count || !isWeekend(result.history[index]?.date)))
  );

  result.history = filteredHistory;
  result.previousHistory = filteredPreviousHistory;
  // if we should show the rank and scope is not the Entire Org
  if (preferences.showRankPosition && (scope?.teamId || scope?.userId)) {
    const response: ListResponse<LeaderboardItem> = yield callApi(
      "fetchLeaderboardData",
      orgId,
      {
        date: filters.createdAt,
        includePerformanceMetric: true,
        property: { $in: [newRecordsMetricByEntityType[criteria.entityType]] },
        summary: isTeamScope ? "team" : "user",
      },
      undefined
    );
    const rank = isTeamScope
      ? response.data.findIndex(({ teamId }) => teamId === scope!.teamId)
      : response.data.findIndex(({ userId }) => userId === scope!.userId);
    result.rank = rank >= 0 ? rank + 1 : undefined;
  }

  yield call(callback, result);
}
